import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatKPIValue, getAccessibleNumber } from '@/lib/number-formatting';

export type KPIVariant = 'sales' | 'orders' | 'products' | 'users' | 'advance' | 'value' | 'activity';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  variant: KPIVariant;
  trend?: {
    value: string;
    icon: LucideIcon;
    positive?: boolean;
  };
  className?: string;
  // Apple UX Guidelines for large numbers
  valueType?: 'currency' | 'number' | 'percentage';
  currency?: string;
  locale?: string;
  compactFormat?: boolean;
}

const variantClasses = {
  sales: 'bg-kpi-sales-bg border-kpi-sales-border',
  orders: 'bg-kpi-orders-bg border-kpi-orders-border',
  products: 'bg-kpi-products-bg border-kpi-products-border',
  users: 'bg-kpi-users-bg border-kpi-users-border',
  advance: 'bg-kpi-advance-bg border-kpi-advance-border',
  value: 'bg-kpi-value-bg border-kpi-value-border',
  activity: 'bg-kpi-activity-bg border-kpi-activity-border',
};

const iconVariantClasses = {
  sales: 'text-kpi-sales-text',
  orders: 'text-kpi-orders-text',
  products: 'text-kpi-products-text',
  users: 'text-kpi-users-text',
  advance: 'text-kpi-advance-text',
  value: 'text-kpi-value-text',
  activity: 'text-kpi-activity-text',
};

export function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  variant,
  trend,
  className,
  valueType = 'number',
  currency = 'GTQ',
  locale = 'es-GT',
  compactFormat = true
}: KPICardProps) {
  // Smart formatting for large numbers following Apple guidelines
  const formatValue = (val: string | number): string => {
    if (typeof val === 'string') return val;
    
    return formatKPIValue(val, valueType, {
      locale,
      currency,
      compact: compactFormat,
      maxCompactDigits: 5 // Apple recommends showing compact notation sooner for KPIs
    });
  };

  // Accessibility support
  const getAriaLabel = (): string => {
    const formattedValue = typeof value === 'number' 
      ? getAccessibleNumber(value, valueType)
      : value;
    
    return `${title}: ${formattedValue}${trend ? `. ${trend.value}` : ''}`;
  };
  return (
    <Card 
      className={cn(
        "hover:shadow-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] bg-card/80 backdrop-blur-xl border-border/20 shadow-xs h-full",
        className
      )}
      role="article"
      aria-label={getAriaLabel()}
    >
      <CardContent className="p-4 flex flex-col h-full">
        {/* Header with icon and title */}
        <div className="flex items-center space-x-3 mb-3">
          <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
          </div>
          <p className="text-xs font-medium text-muted-foreground leading-tight" id={`kpi-title-${variant}`}>
            {title}
          </p>
        </div>

        {/* Main value - Optimized for large numbers with Apple guidelines */}
        <div className="flex-1 flex flex-col justify-center min-h-0">
          <div 
            className="text-2xl font-bold text-foreground tracking-tight leading-none mb-1 font-mono tabular-nums"
            style={{
              // Apple's SF Mono characteristics for numbers
              fontFeatureSettings: '"tnum" 1, "zero" 1',
              letterSpacing: '-0.02em'
            }}
            aria-describedby={`kpi-title-${variant}`}
          >
            {formatValue(value)}
          </div>
          {subtitle && (
            <p className="text-xs text-muted-foreground font-medium leading-tight truncate">
              {subtitle}
            </p>
          )}
        </div>

        {/* Trend indicator */}
        {trend && (
          <div className="flex items-center space-x-2 pt-2 mt-auto border-t border-border/20">
            <div className="h-5 w-5 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <trend.icon 
                className={cn(
                  "h-3 w-3",
                  trend.positive === true ? "text-emerald-600" :
                  trend.positive === false ? "text-red-500" : "text-primary"
                )}
                aria-hidden="true"
              />
            </div>
            <span className="text-xs font-medium text-muted-foreground leading-tight truncate">
              {trend.value}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}